/**
 * Labstep
 *
 * @module components/Metadata/Action/AddFromRelation
 * @desc Action to add metadata fields from a relation target
 */

import { EntityCreateContainer } from 'labstep-web/containers/Entity/Create';
import { ActionComponentModalContainer as ActionComponent } from 'labstep-web/core/Action/Component';
import { Flex } from 'labstep-web/core/Flex';
import { ModalDefaultContainer as ModalDefault } from 'labstep-web/core/Modal/Default';
import { ReadOnMountHOC } from 'labstep-web/hoc/ReadOnMount';
import { Metadata } from 'labstep-web/models/metadata';
import { MetadataType } from 'labstep-web/models/metadata/types';
import { MetadataThread } from 'labstep-web/models/metadata-thread.model';
import { Resource } from 'labstep-web/models/resource.model';
import type React from 'react';
import { useState } from 'react';
import type { AddFromRelationProps } from './types';

export const MetadataActionAddFromRelation: React.FC<
  AddFromRelationProps
> = ({ metadata, entity, actionComponentProps }) => {
  const [selectedFields, setSelectedFields] = useState<Metadata[]>(
    [],
  );

  const handleFieldToggle = (field: Metadata) => {
    setSelectedFields((prev) =>
      prev.find((f) => f.id === field.id)
        ? prev.filter((f) => f.id !== field.id)
        : [...prev, field],
    );
  };

  const targetTemplateId = metadata.field_definition
    ?.entity_relation_target_entity_filter?.template_id as string;

  if (
    !targetTemplateId ||
    metadata.type !== MetadataType.entity_relation
  ) {
    return null;
  }

  const existingFields = entity.metadata_thread.metadatas.filter(
    (met) =>
      met.field_definition?.lookup_relation_field_definition?.id ===
      metadata.field_definition?.id,
  );

  return (
    <EntityCreateContainer
      entityName={Metadata.entityName}
      parentId={entity.metadata_thread.idAttr}
      parentName={MetadataThread.entityName}
      batch
    >
      {({ create, status }) => (
        <ModalDefault
          size="small"
          header="Add Metadata from Relation"
          viewComponent={({ toggleModal }): React.ReactElement => (
            <ActionComponent
              type="option"
              text="Add Metadata from Relation"
              icon="plus"
              onClick={toggleModal}
              {...actionComponentProps}
            />
          )}
          actions={({ toggleModal }): React.ReactElement => (
            <Flex spacing="gap">
              <ActionComponent
                text="Cancel"
                type="button"
                elementProps={{ basic: true }}
                onClick={() => {
                  toggleModal();
                  setSelectedFields([]);
                }}
              />
              <ActionComponent
                text={`Add ${selectedFields.length} Selected Fields`}
                type="button"
                status={status}
                disabled={selectedFields.length === 0}
                onClick={() => {
                  const metadataPayloads = selectedFields.map(
                    (field) => ({
                      label: field.label,
                      field_definition: {
                        name: field.label,
                        data_type: field.type,
                        lookup_relation_field_definition_id:
                          metadata.field_definition?.id,
                      },
                    }),
                  );

                  create(metadataPayloads, {
                    toast: true,
                    onSuccess: () => {
                      toggleModal();
                      setSelectedFields([]);
                    },
                  });
                }}
              />
            </Flex>
          )}
          content={({ toggleModal }): React.ReactElement => (
            <ReadOnMountHOC
              type="entity"
              entityName={Resource.entityName}
              loading={{ loader: 'placeholder', cached: true }}
              id={targetTemplateId}
            >
              {({ entity: targetTemplate }) => {
                const availableFields =
                  targetTemplate?.metadata_thread?.metadatas?.filter(
                    (relatedMetadata: Metadata) =>
                      relatedMetadata.label &&
                      relatedMetadata.type !==
                        MetadataType.entity_relation,
                  ) || [];

                return (
                  <div>
                    <p>
                      Select metadata fields to add from{' '}
                      <strong>{targetTemplate?.name}</strong>:
                    </p>
                    <div
                      style={{
                        margin: '15px 0',
                        maxHeight: '300px',
                        overflowY: 'auto',
                      }}
                    >
                      {availableFields.map(
                        (relatedMetadata: Metadata) => (
                          <div
                            key={relatedMetadata.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '8px 0',
                              borderBottom: '1px solid #eee',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={
                                selectedFields.find(
                                  (f) => f.id === relatedMetadata.id,
                                ) !== undefined ||
                                existingFields.find(
                                  (f) =>
                                    f.label === relatedMetadata.label,
                                ) !== undefined
                              }
                              onChange={() =>
                                handleFieldToggle(relatedMetadata)
                              }
                              disabled={
                                existingFields.find(
                                  (f) =>
                                    f.label === relatedMetadata.label,
                                ) !== undefined
                              }
                            />
                            <span
                              style={{ flex: 1, cursor: 'pointer' }}
                              onClick={() => {
                                existingFields.find(
                                  (f) =>
                                    f.label === relatedMetadata.label,
                                ) === undefined &&
                                  handleFieldToggle(relatedMetadata);
                              }}
                            >
                              <strong>{relatedMetadata.label}</strong>
                            </span>
                            <span
                              style={{
                                fontSize: '0.8em',
                                color: '#666',
                                textTransform: 'capitalize',
                              }}
                            >
                              {relatedMetadata.type.replace('_', ' ')}
                            </span>
                          </div>
                        ),
                      )}
                      {availableFields.length === 0 && (
                        <p
                          style={{
                            color: '#666',
                            fontStyle: 'italic',
                          }}
                        >
                          No available fields found on this template.
                        </p>
                      )}
                    </div>
                    <p
                      style={{
                        fontSize: '0.9em',
                        color: '#666',
                        marginBottom: '20px',
                      }}
                    >
                      The value of these fields will be looked up from
                      the related entity when relations are created.
                    </p>
                  </div>
                );
              }}
            </ReadOnMountHOC>
          )}
        />
      )}
    </EntityCreateContainer>
  );
};
